import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useServiceProviderStore } from '@/stores/service-provider';
import { useClientProjectStore } from '@/stores/service-provider/client-project-store';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ServiceProviderProfileForm, ServiceProviderProfileFormData } from '@/components/service-provider/ServiceProviderProfileForm';
import ClientProjectCard from '@/components/service-provider/ClientProjectCard';
import { Pencil, Plus, Loader2, Wrench } from 'lucide-react';

const TIER_LABELS: Record<string, string> = {
  registered: 'Registered',
  certified: 'Certified',
  qualified_partner: 'Qualified Partner',
  ambassador: 'Ambassador',
};

const TIER_COLORS: Record<string, string> = {
  registered: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  certified: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  qualified_partner: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  ambassador: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
};

const SPECIALIZATION_LABELS: Record<string, string> = {
  electrical: 'Electrical',
  plumbing: 'Plumbing',
  hvac: 'HVAC',
  security: 'Security',
  networking: 'Networking',
  solar: 'Solar',
  av: 'Audio/Visual',
  iot: 'IoT / Smart Home',
  design: 'Design',
  lighting: 'Lighting',
  landscaping: 'Landscaping',
  automation: 'Automation',
  general: 'General',
  marketing: 'Marketing',
  sales: 'Sales',
  consulting: 'Consulting',
  business_strategy: 'Business Strategy',
  accounting: 'Accounting',
  it_services: 'IT Services',
  web_development: 'Web Development',
  software: 'Software',
  cybersecurity: 'Cybersecurity',
  data_analytics: 'Data Analytics',
  graphic_design: 'Graphic Design',
  content_creation: 'Content Creation',
  photography: 'Photography',
  video_production: 'Video Production',
  other: 'Other',
};

const ServiceProviderDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { profile, isLoading, error, fetchProfile, updateProfile, clearError } = useServiceProviderStore();
  const { clientProjects, isLoading: clientProjectsLoading, fetchClientProjects } = useClientProjectStore();
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchClientProjects();
  }, [fetchProfile, fetchClientProjects]);

  // Redirect to setup if no profile after loading
  useEffect(() => {
    if (!isLoading && profile === null && useServiceProviderStore.getState().lastFetched !== null) {
      navigate('/service-provider/setup', { replace: true });
    }
  }, [isLoading, profile, navigate]);

  const handleEditSubmit = async (data: ServiceProviderProfileFormData) => {
    try {
      setEditSaving(true);
      await updateProfile(data);
      setEditOpen(false);
    } catch {
      // Error is set in store
    } finally {
      setEditSaving(false);
    }
  };

  if (isLoading && !profile) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl py-8 px-4">
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const editDefaults: Partial<ServiceProviderProfileFormData> = {
    specializations: profile.specializations,
    otherSpecialization: profile.otherSpecialization || '',
    businessName: profile.businessName || '',
    businessDescription: profile.businessDescription || '',
    businessPhone: profile.businessPhone || '',
    businessEmail: profile.businessEmail || '',
    businessWebsite: profile.businessWebsite || '',
    employeeCount: profile.employeeCount,
    businessAddress: {
      street: profile.businessAddress?.street || '',
      city: profile.businessAddress?.city || '',
      state: profile.businessAddress?.state || '',
      postalCode: profile.businessAddress?.postalCode || '',
      country: profile.businessAddress?.country || '',
    },
  };

  return (
    <div className="mx-auto max-w-4xl py-8 px-4">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Service Provider Dashboard</h1>
          {profile.businessName && (
            <p className="text-muted-foreground">{profile.businessName}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit Profile
          </Button>
          <Badge className={TIER_COLORS[profile.certificationTier] || ''}>
            {TIER_LABELS[profile.certificationTier] || profile.certificationTier}
          </Badge>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Active Engagements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{profile.activeEngagementCount}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Completed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{profile.completedEngagements}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Rating
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">
              {profile.ratings.count > 0
                ? `${profile.ratings.average.toFixed(1)}/5`
                : 'N/A'}
            </p>
            {profile.ratings.count > 0 && (
              <p className="text-sm text-muted-foreground">
                {profile.ratings.count} review{profile.ratings.count !== 1 ? 's' : ''}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Specializations */}
      <div className="mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Specializations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {profile.specializations.map((specialization) => (
                <Badge key={specialization} variant="outline" className="capitalize">
                  {specialization === 'other' && profile.otherSpecialization
                    ? profile.otherSpecialization
                    : (SPECIALIZATION_LABELS[specialization] || specialization)}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Business Info */}
      {(profile.businessPhone || profile.businessEmail || profile.businessWebsite) && (
        <div className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Business Info</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2">
                {profile.businessPhone && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Phone</p>
                    <p className="text-sm">{profile.businessPhone}</p>
                  </div>
                )}
                {profile.businessEmail && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Email</p>
                    <p className="text-sm">{profile.businessEmail}</p>
                  </div>
                )}
                {profile.businessWebsite && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Website</p>
                    <p className="text-sm">{profile.businessWebsite}</p>
                  </div>
                )}
                {profile.employeeCount && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Employees</p>
                    <p className="text-sm">{profile.employeeCount}</p>
                  </div>
                )}
                {profile.businessAddress?.street && (
                  <div className="sm:col-span-2">
                    <p className="text-sm font-medium text-muted-foreground">Address</p>
                    <p className="text-sm">
                      {profile.businessAddress.street}
                      {profile.businessAddress.city && `, ${profile.businessAddress.city}`}
                      {profile.businessAddress.state && `, ${profile.businessAddress.state}`}
                      {profile.businessAddress.postalCode && ` ${profile.businessAddress.postalCode}`}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Client Projects */}
      <div className="mt-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Client Projects</CardTitle>
                <CardDescription>
                  Manage client projects and configurations
                </CardDescription>
              </div>
              <Button onClick={() => navigate('/service-provider/projects/new')} size="sm">
                <Plus className="mr-2 h-4 w-4" />
                New Client Project
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {clientProjectsLoading && clientProjects.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : clientProjects.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No client projects yet. Create your first client project to get started.
              </p>
            ) : (
              <div className="space-y-3">
                {clientProjects.map((clientProject) => (
                  <ClientProjectCard key={clientProject._id} clientProject={clientProject} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Profile Dialog */}
      <Dialog open={editOpen} onOpenChange={(open) => !open && setEditOpen(false)}>
        <DialogContent variant="light" className="sm:max-w-2xl p-0 gap-0">
          {/* Header */}
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50">
                <Wrench className="h-4 w-4 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Edit Service Provider Profile</h2>
                <p className="text-[11px] text-gray-500">
                  Update your specializations and business information
                </p>
              </div>
            </div>
          </div>

          <div className="border-b border-gray-100" />

          {/* Scrollable form body */}
          <div className="overflow-y-auto px-4 py-4" style={{ maxHeight: '70vh' }}>
            {editOpen && (
              <ServiceProviderProfileForm
                onSubmit={handleEditSubmit}
                defaultValues={editDefaults}
                isLoading={editSaving}
                submitLabel="Save Changes"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceProviderDashboardPage;
